import { useCallback, useEffect, useRef } from 'react';
import { FEATURE_FLAGS } from '@brandschutz/shared';
import { useIsFeatureEnabled } from '@/lib/feature-flags';
import { useToast } from '@/components/ui/Toast';
import { MikrofonKnopf } from './MikrofonKnopf';
import { useDiktat } from './use-diktat';
import { FEHLER_TEXTE, spracherkennungVerfuegbar } from './web-speech';

/**
 * Diktat-Knopf für ein einzelnes Textfeld — das Stück, das an die
 * Formularfelder gehängt wird (siehe README.md).
 *
 * Der Knopf entscheidet selbst, ob er überhaupt erscheint: ohne Feature-Flag
 * oder ohne Spracherkennung im Browser rendert er nichts. Aufrufer müssen
 * deshalb nichts prüfen und können ihn bedingungslos neben jedes Feld setzen.
 *
 * Er ist immer ZUSATZ, nie Ersatz: Tippen, Einfügen und Tastaturbedienung des
 * Feldes bleiben unangetastet.
 */

export interface DiktatKnopfProps {
  /** Aktueller Feldwert. */
  wert: string;
  /** Bekommt den vollständigen neuen Wert inklusive des Diktierten. */
  onWert: (neu: string) => void;
  /** Feldname für Tooltip und Screenreader, z. B. „Beschreibung“. */
  feldName?: string;
  /** Read-Only-Felder (z. B. abgeschlossene Projekte) diktiert man nicht. */
  deaktiviert?: boolean;
  kompakt?: boolean;
  mitBeschriftung?: boolean;
  className?: string;
}

export function DiktatKnopf({
  wert,
  onWert,
  feldName,
  deaktiviert = false,
  kompakt = true,
  mitBeschriftung = false,
  className,
}: DiktatKnopfProps) {
  const aktiviert = useIsFeatureEnabled(FEATURE_FLAGS.VOICE_CONTROL);
  const toast = useToast();

  // Werte über Refs: der Erkenner-Callback läuft außerhalb des Renderzyklus
  // und würde sonst mit dem Feldstand von vor der Aufnahme arbeiten.
  const wertRef = useRef(wert);
  wertRef.current = wert;
  const onWertRef = useRef(onWert);
  onWertRef.current = onWert;

  const leseWert = useCallback(() => wertRef.current, []);
  const schreibeWert = useCallback((neu: string) => onWertRef.current(neu), []);

  const diktat = useDiktat({ wert: leseWert, onWert: schreibeWert });

  // Fehler gehören dorthin, wo der Nutzer ohnehin hinschaut — nicht als
  // stiller Zustand am Knopf. Besonders die verweigerte Mikrofonfreigabe
  // braucht eine Erklärung, sonst passiert scheinbar einfach nichts.
  const letzterFehlerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!diktat.fehler || diktat.fehler === letzterFehlerRef.current) {
      if (!diktat.fehler) letzterFehlerRef.current = null;
      return;
    }
    letzterFehlerRef.current = diktat.fehler;
    toast.warning('Diktat', FEHLER_TEXTE[diktat.fehler]);
  }, [diktat.fehler, toast]);

  // Wird das Feld gesperrt, während es zuhört, muss das Mikrofon aus.
  const { status, stoppe } = diktat;
  useEffect(() => {
    if (deaktiviert && status === 'hoert-zu') stoppe();
  }, [deaktiviert, status, stoppe]);

  // Aus statt kaputt: ohne Flag oder ohne Browser-Unterstützung gibt es den
  // Knopf gar nicht, statt in jedem Feld ein totes Symbol zu zeigen.
  if (!aktiviert || !spracherkennungVerfuegbar()) return null;

  return (
    <MikrofonKnopf
      status={diktat.status}
      onKlick={diktat.umschalten}
      bezeichnung={feldName}
      kompakt={kompakt}
      mitBeschriftung={mitBeschriftung}
      deaktiviert={deaktiviert}
      className={className}
    />
  );
}
